import { createFileRoute } from '@tanstack/react-router'
import { Panel } from '@/components/panel'
import { useOps } from '@/lib/admin'
import { formatExact } from '@/lib/format'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/admin/queues')({
  component: QueuesPage,
})

/**
 * Whether the worker is keeping up.
 *
 * Read from pg-boss rather than from our own tables, because the failure this
 * page exists to catch is the one our tables cannot show: a queue that has
 * stopped being consumed looks identical to a quiet week from the fact tables,
 * which simply stop growing either way.
 *
 * The counts are not the finding — the *ages* are. A thousand queued jobs
 * draining steadily is a backfill working; forty queued jobs whose oldest has
 * been waiting since yesterday is a worker that has died.
 */
function QueuesPage() {
  const { data, isPending } = useOps()

  if (isPending || !data) return <p className="text-[13px] text-muted-foreground">Loading…</p>

  const now = Date.parse(data.now)
  const backlog = data.queues.reduce((total, queue) => total + queue.queued + queue.retrying, 0)
  const failing = data.queues.reduce((total, queue) => total + queue.failed, 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Tile label="Waiting" value={formatExact(backlog)} />
        <Tile label="Running" value={formatExact(data.queues.reduce((t, q) => t + q.active, 0))} />
        <Tile label="Failed" value={formatExact(failing)} tone={failing > 0 ? 'bad' : undefined} />
        <Tile label="Schedules" value={String(data.schedules.length)} />
      </div>

      <Panel title="Queues" meta={`${data.queues.length}`} flush>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <Th className="text-left">Queue</Th>
                <Th>Waiting</Th>
                <Th>Running</Th>
                <Th>Retrying</Th>
                <Th>Failed</Th>
                <Th>Oldest wait</Th>
                <Th>Last finished</Th>
              </tr>
            </thead>
            <tbody>
              {data.queues.map((queue) => (
                <tr key={queue.name} className="border-b border-border last:border-b-0">
                  <td className="py-2 pr-3 pl-3 font-mono text-[12.5px]">{queue.name}</td>
                  <Td value={queue.queued} />
                  <Td value={queue.active} />
                  <Td value={queue.retrying} tone={queue.retrying > 0 ? 'warn' : undefined} />
                  <Td value={queue.failed} tone={queue.failed > 0 ? 'bad' : undefined} />
                  <td className="px-3 py-2 text-right font-mono text-[12px] tabular">
                    <Age at={queue.oldestQueued} now={now} />
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-[12px] text-muted-foreground tabular">
                    <Age at={queue.lastCompleted} now={now} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Scheduled" flush>
          {/* Straight from pg-boss, not from our source: `schedule` is an upsert
              that does nothing on conflict, so what is in the repo and what is
              actually firing can disagree — and this is the one that fires. */}
          <ul className="divide-y divide-border">
            {data.schedules.map((schedule) => (
              <li key={schedule.name} className="flex items-baseline gap-3 px-4 py-2 text-[12.5px]">
                <span className="min-w-0 flex-1 truncate font-mono">{schedule.name}</span>
                <span className="shrink-0 font-mono text-muted-foreground tabular">
                  {schedule.cron}
                </span>
                <span className="mono-label shrink-0 text-muted-foreground">
                  {schedule.timezone}
                </span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Recent failures" meta={formatExact(data.failures.length)} flush>
          {data.failures.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-muted-foreground">
              Nothing has failed.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {data.failures.map((failure) => (
                <li key={failure.id} className="space-y-1 px-4 py-2.5 text-[12.5px]">
                  <div className="flex items-baseline gap-3">
                    <span className="min-w-0 flex-1 truncate font-mono">{failure.name}</span>
                    <span className="mono-label shrink-0 text-muted-foreground">
                      {failure.retryCount} tries
                    </span>
                    <span className="shrink-0 font-mono text-[11px] text-muted-foreground tabular">
                      <Age at={failure.failedAt} now={now} />
                    </span>
                  </div>
                  {/* The message, verbatim. "2 failed" is a number nobody can
                      act on; "Could not resolve docs.example.com" is a fix. */}
                  {failure.message && (
                    <p className="font-mono text-[11.5px] text-destructive">{failure.message}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  )
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="mono-label text-muted-foreground">{label}</p>
      <p
        className={cn(
          'pt-2 font-mono text-[24px] leading-none tabular',
          tone === 'bad' && 'text-destructive',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn('px-3 py-2 text-right', className)}>
      <span className="mono-label text-muted-foreground">{children}</span>
    </th>
  )
}

function Td({ value, tone }: { value: number; tone?: 'warn' | 'bad' }) {
  return (
    <td className="px-3 py-2 text-right align-middle">
      <span
        className={cn(
          'font-mono tabular',
          value === 0 && 'text-muted-foreground/50',
          tone === 'warn' && 'text-warning',
          tone === 'bad' && 'text-destructive',
        )}
      >
        {value}
      </span>
    </td>
  )
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * Compact and absolute rather than "3 minutes ago": this table is read by
 * scanning a column for the outlier, and a column of prose does not scan.
 * Measured against the server's clock, which is the one that owns the queue.
 */
function Age({ at, now }: { at: string | null; now: number }) {
  if (!at) return <span className="text-muted-foreground/50">—</span>

  const elapsed = now - Date.parse(at)
  if (!Number.isFinite(elapsed)) return <span className="text-muted-foreground/50">—</span>
  if (elapsed < MINUTE) return <>now</>
  if (elapsed < HOUR) return <>{Math.round(elapsed / MINUTE)}m</>
  if (elapsed < DAY) return <>{Math.round(elapsed / HOUR)}h</>
  return <>{Math.round(elapsed / DAY)}d</>
}
